/**
 * billing.ts — VIREON Cortex TGSPDCL HT Billing Engine
 * ======================================================
 * Builds a complete bill breakdown from a window of TelemetryRows.
 *
 * Tariff: Southern Power Distribution Company of Telangana Limited (TGSPDCL)
 * Category: HT Consumer, Cat 1A, 33 kV supply
 * Reference: VIREON-CORTEX Variables & Formulas PDF §3, §4.
 *
 * Key differences from WB tariff:
 *  - Energy charges are per kVAh (not kWh)
 *  - Three-tier rates based on MD vs CD ratio (PDF §3.1–3.3)
 *  - Demand: Rs 500/kVA (normal), Rs 1000/kVA (penal excess)
 *  - Minimum demand = 80% of CD (per sample bills)
 *  - ED = 6 paise / kVAh flat (not %)
 *  - Customer charges = Rs 3500/month (flat)
 *  - No LF rebate, no PF rebate table, no meter rental, no TPR
 */

import type { TelemetryRow } from '@/types';
import type { StateTariff, ToDPeriod } from '@/tariffs';
import { getTariff } from '@/tariffs';
import { energyDelivered, safe, isValidPfReading, loadFactor, maxDemandKvaOf, fifteenMinDemandBlocks, MD_BLOCK_MS_30 } from './calculations';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface BillPeriodBreakdown {
  period:         ToDPeriod;
  /** kVAh consumed in this ToD period (from VAh registers). */
  kvah:           number;
  rateRsKvah:     number;
  energyChargeRs: number;
  /** Weighted/avg PF for THIS ToD period only (drives the per-period slab). */
  pfAvg:          number;
  /** @deprecated No PF adjustment in TGSPDCL HT — always 0. */
  pfAdjPct:       number;
  pfAdjRs:        number;
  /** @deprecated Kept for interface compatibility. */
  kwh:            number;
  rateRsKwh:      number;
}

export interface BillBreakdown {
  spanHours:     number;
  monthFraction: number;

  periods:       BillPeriodBreakdown[];
  ecTotalRs:     number;

  pfWeighted:    number;
  /** Always 0 — TGSPDCL HT does not use a PF rebate table. */
  pfAdjTotalRs:  number;

  /** Always 0 — TGSPDCL HT has no LF rebate. */
  lfPct:         number;
  lfRebateRs:    number;

  /** Billing-cycle max demand kVA. */
  peakKva:        number;
  /** PDF BE: Max Demand kVA during NORMAL ToD period. WB only; 0 for Telangana. */
  mdKvaNormal:    number;
  /** PDF BF: Max Demand kVA during PEAK ToD period. WB only; 0 for Telangana. */
  mdKvaPeak:      number;
  /** PDF BG: Max Demand kVA during OFF-PEAK ToD period. WB only; 0 for Telangana. */
  mdKvaOffpeak:   number;
  /** Normal chargeable kVA = max(peakKva, 80% CD), clamped to CD. */
  normalKva:      number;
  /** Penal kVA = max(0, peakKva − CD). */
  addlKva:        number;
  /** Normal demand charge (Rs). */
  dcNormalRs:     number;
  /** Penal demand charge for excess above CD (Rs). */
  dcPenalRs:      number;
  dcRs:           number;

  electricityDutyRs: number;
  customerChargesRs: number;
  /** ACD (Additional Consumption Deposit) surcharge, Rs. TGSPDCL only; 0 otherwise. */
  acdSurchargeRs:    number;
  /** Always 0 — no meter rental. */
  meterRentalRs:  number;

  subTotalRs:         number;
  grossPayableRs:     number;
  /** Late payment surcharge (1.25% of gross) — informational only, applied if delayed. */
  lpscRs:         number;
  netPayableRs:   number;

  /** Applied energy rate tier label. */
  rateTierLabel:  string;

  /** Total kVAh consumed across all ToD periods. */
  totalKvah:      number;
  /** Total kWh consumed (non-kVAh billing). 0 when billing in kVAh. */
  totalKwh:       number;

  /** Total demand charge = dcNormalRs + dcPenalRs (Rs). */
  dcTotalRs:      number;

  // ── Telangana ToD breakdown (TGSPDCL billing model) ───────────────────────
  /** MTD Peak kVAh (06:00–10:00 + 18:00–22:00). 0 for WB. */
  todPeakKvah:        number;
  /** MTD Off-Peak kVAh (22:00–06:00). 0 for WB. */
  todOffpeakKvah:     number;
  /** TGSPDCL: Base EC = totalKvah × Normal rate. = ecTotalRs for WB. */
  ecBaseRs:           number;
  /** TGSPDCL: ToD Peak Penalty = peakKvah × (peakRate − normalRate). 0 for WB. */
  todPeakPenaltyRs:   number;
  /** TGSPDCL: ToD Off-Peak Rebate = offpeakKvah × (normalRate − offpeakRate). 0 for WB. */
  todOffpeakRebateRs: number;

  /** Projected month-end bill (Rs). */
  projectedBillRs?: number;
  projectedKvah?:   Record<ToDPeriod, number>;
  projectedGrossEcRs?: number;
  projectedPfAdjRs?: number;
  projectedLfAdjRs?: number;
  projectedEdRs?: number;
  projectedTprRs?: number;

  // Legacy compat aliases
  mdPenaltyRs:    number;
  chargeableKva:  number;
  netBeforeEdRs:  number;
  tprRs:          number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function weightedPF(rows: TelemetryRow[]): number {
  if (rows.length < 2) {
    const valid = rows.filter(isValidPfReading);
    if (!valid.length) return 0;
    return Math.min(
      valid.reduce((sum, r) => sum + safe(r.pfAvg), 0) / valid.length,
      0.999,
    );
  }
  const sorted    = [...rows].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  // energyWh  = raw Wh register (whReceived) → divide by 1000 to get kWh.
  const deltaKwh  = Math.max(0, safe(sorted[sorted.length - 1].energyWh)  - safe(sorted[0].energyWh))  / 1000;
  // energyVAh = raw VAh register (vahReceived) — same unit scale as energyWh (raw Wh).
  // Must also divide by 1000 to get kVAh so PF = kWh / kVAh stays in (0, 1].
  // Evidence: derivedEngine.ts line 481: energyVAhDerived = energyVahCurrent / 1000 (→ kVAh),
  // and runningAvgPF = energyWhCurrent / energyVahCurrent (both raw, ratio = 0.961 ✓).
  // Previous comment "stored as kVAh — do NOT divide" was incorrect and produced
  // PF = kWh / VAh ≈ 0.001 (1000× too small → effectively 0 → broken PF slabs).
  const deltaKvah = Math.max(0, safe(sorted[sorted.length - 1].energyVAh) - safe(sorted[0].energyVAh)) / 1000;
  if (deltaKvah > 0) return Math.min(deltaKwh / deltaKvah, 1);
  const valid = rows.filter(isValidPfReading);
  if (!valid.length) return 0;
  return Math.min(valid.reduce((sum, r) => sum + safe(r.pfAvg), 0) / valid.length, 0.999);
}

function periodRows(rows: TelemetryRow[], period: ToDPeriod): TelemetryRow[] {
  return rows
    .filter((r) => r.todPeriod === period)
    .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
}

/**
 * kVAh delivered in a window — trapezoidal integration of kvaTotal (kVA).
 *
 * WHY trapezoidal (not register delta):
 *   Register-delta (last.energyVAh − first.energyVAh) is WRONG for per-period
 *   filtered rows spanning multiple days: the cumulative VAh register grows
 *   continuously through ALL ToD periods, so the delta from the first Normal row
 *   of Day 1 (10:00) to the last Normal row of Day 2 (18:00) captures 32 hours of
 *   total consumption, not 16 hours of Normal-only energy.
 *
 *   Trapezoidal integration naturally handles this: the day-boundary gap from
 *   Day 1 18:00 (last Normal row) to Day 2 10:00 (first Normal row) is 16 h and
 *   is skipped by the >1h guard, so only the actual within-period hours count.
 *
 * SOURCE OF kvaTotal:
 *   derivedEngine.ts computes kvaTotal = min(apparentVa(row), vaCeiling) / 1000,
 *   where apparentVa = Watts_Total / PF_Avg (falls back to VA_Total when PF invalid).
 *   This is the billing-correct instantaneous apparent power in kVA.
 *
 * RESULT UNIT: the integral of kVA over hours = kVAh. ✓
 */
function kvahDelivered(rows: TelemetryRow[]): number {
  if (rows.length < 2) return 0;
  let kvah = 0;
  for (let i = 1; i < rows.length; i++) {
    const dtH = (rows[i].timestamp.getTime() - rows[i - 1].timestamp.getTime()) / 3_600_000;
    if (dtH <= 0 || dtH > 1) continue; // skip gaps >1h (day boundaries, outages)
    kvah += ((safe(rows[i - 1].kvaTotal) + safe(rows[i].kvaTotal)) / 2) * dtH;
  }
  return kvah; // kVAh
}

/**
 * kVAh delivered over a window from the cumulative VAh REGISTER (vahReceived).
 *
 * WHY register-delta for the TOTAL (not the trapezoidal integral):
 *   TGSPDCL bills the physical meter's VAh register difference
 *   (closing − opening). The register keeps counting through data-logger
 *   outages, so it captures energy consumed during gaps that the trapezoidal
 *   integral silently drops (its `dtH > 1` guard discards any interval longer
 *   than an hour). On months with >1h logger gaps the integral under-reads the
 *   register by several % — which is exactly why our estimate came in below the
 *   official invoice.
 *
 * Robustness:
 *   - Rows whose register reads 0 are logger dropouts (not a real meter reset);
 *     they are skipped so the huge 0→real rebound isn't counted as consumption.
 *   - We sum only POSITIVE consecutive deltas across the surviving rows, so a
 *     genuine mid-cycle meter rollover/replacement can't produce a negative.
 *   - Equals (last − first)/1000 for a clean monotonic month.
 *
 * NOTE: this is the TOTAL kVAh. The per-ToD split still comes from the
 * trapezoidal integral (kvahDelivered) — see buildBillBreakdown, where the
 * integral is used only as a ratio and rescaled to this register total.
 */
function registerKvahDelivered(rows: TelemetryRow[]): number {
  const valid = [...rows]
    .filter((r) => safe(r.energyVAh) > 0)
    .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  if (valid.length < 2) return 0;
  let vah = 0;
  for (let i = 1; i < valid.length; i++) {
    const d = safe(valid[i].energyVAh) - safe(valid[i - 1].energyVAh);
    if (d > 0) vah += d;
  }
  return vah / 1000; // kVAh
}

// ─── Main function ─────────────────────────────────────────────────────────────

/**
 * Build a complete bill breakdown for the given telemetry window, using the
 * supplied state tariff. ToD bucketing reads each row's `todPeriod` (set by the
 * state-aware derived engine), and all rates/charges follow the tariff:
 *   • energy register: kWh (WBSEDCL) or kVAh (TGSPDCL)
 *   • PF rebate table (WBSEDCL) / RKVAH (TGSPDCL — no % table)
 *   • LF rebate (WBSEDCL) ; demand, ED, fixed charges per model
 *
 * @param rows        Sanitised TelemetryRows for the billing window (any order).
 * @param daysPassed  Days elapsed since billing period start (for projection).
 * @param daysTotal   Total days in the billing month (for projection).
 * @param contractDemandKvaOverride  Override CD if different from default.
 * @param tariff      The state tariff config (defaults to West Bengal).
 * @param acdSurchargeRsOverride  Per-consumer ACD surcharge (Rs/cycle). Overrides
 *   tariff.acdSurchargeRs; deposit-linked, not telemetry-derived. TGSPDCL only.
 */
export function buildBillBreakdown(
  rows: TelemetryRow[],
  daysPassed?: number,
  daysTotal?: number,
  contractDemandKvaOverride?: number,
  tariff: StateTariff = getTariff('west-bengal'),
  acdSurchargeRsOverride?: number,
): BillBreakdown {
  const CD     = contractDemandKvaOverride ?? tariff.defaultContractDemandKva;
  const minCD  = CD * tariff.minDemandPct;
  const isKvah = tariff.energyUnit === 'kvah';

  /** Energy delivered in a window on the state's billing register. */
  const unitDelivered = (rs: TelemetryRow[]): number =>
    isKvah ? kvahDelivered(rs) : energyDelivered(rs);

  const EMPTY: BillBreakdown = {
    spanHours: 0, monthFraction: 0,
    periods: [], ecTotalRs: 0,
    pfWeighted: 0, pfAdjTotalRs: 0,
    lfPct: 0, lfRebateRs: 0,
    peakKva: 0, mdKvaNormal: 0, mdKvaPeak: 0, mdKvaOffpeak: 0,
    normalKva: Math.round(minCD), addlKva: 0,
    dcNormalRs: 0, dcPenalRs: 0, dcRs: 0, dcTotalRs: 0,
    electricityDutyRs: 0, customerChargesRs: 0, acdSurchargeRs: 0, meterRentalRs: 0,
    subTotalRs: 0, grossPayableRs: 0, lpscRs: 0, netPayableRs: 0,
    rateTierLabel: tariff.getTierLabel(0, CD),
    totalKvah: 0, totalKwh: 0,
    // Telangana ToD breakdown fields
    todPeakKvah: 0, todOffpeakKvah: 0,
    ecBaseRs: 0, todPeakPenaltyRs: 0, todOffpeakRebateRs: 0,
    // Legacy
    mdPenaltyRs: 0, chargeableKva: Math.round(minCD),
    netBeforeEdRs: 0, tprRs: 0,
  };

  if (rows.length < 2) return EMPTY;

  const sorted      = [...rows].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  const spanHours   = (sorted[sorted.length - 1].timestamp.getTime() - sorted[0].timestamp.getTime()) / 3_600_000;
  const monthFraction = spanHours / (30 * 24);

  // ── Max demand ────────────────────────────────────────────────────────────
  // Use the same 15-min block-averaging formula as ContractDemandCard "Peak kVA".
  // fifteenMinDemandBlocks() splits every inter-sample interval across clock-aligned
  // 15-min windows, computes kVAh ÷ hours for each block, and returns the maximum.
  // This is the billing-correct MD and matches what the dashboard displays.
  // ── Max demand ────────────────────────────────────────────────────────────────────────────────
  // TGSPDCL: single overall max (original behaviour, unchanged).
  // WBSEDCL: per-period max demand (PDF BE/BF/BG) — classify each 15-min block
  //   by the ToD of its START time, take the max avg kVA per period.
  //   PDF BH: overall max = max(BE, BF, BG).
  let mdKvaNormal  = 0;
  let mdKvaPeak    = 0;
  let mdKvaOffpeak = 0;
  let peakKva: number;

  if (!isKvah) {
    // WB path: per-ToD block classification (PDF BE / BF / BG)
    const allBlocks = fifteenMinDemandBlocks(sorted);
    for (const blk of allBlocks) {
      const blockPeriod = tariff.todPeriodFor(new Date(blk.ts).getHours());
      if      (blockPeriod === 'NORMAL')   { if (blk.kva > mdKvaNormal)  mdKvaNormal  = blk.kva; }
      else if (blockPeriod === 'PEAK')     { if (blk.kva > mdKvaPeak)    mdKvaPeak    = blk.kva; }
      else                                 { if (blk.kva > mdKvaOffpeak) mdKvaOffpeak = blk.kva; }
    }
    peakKva = Math.max(mdKvaNormal, mdKvaPeak, mdKvaOffpeak); // PDF BH
  } else {
    // TGSPDCL path: bill Max Demand on a 30-MINUTE integration period (TGSPDCL
    // standard) — NOT 15-min. A 15-min peak over-reads a 30-min MD, which was
    // inflating billed demand (1389 vs the meter's 1236 kVA → +Rs 76,500).
    //
    // Prefer the meter's OWN hardware Max_Demand register when the Pi logs it
    // (maxDemandMeter > 0): that is exactly the value the DISCOM bills, so no
    // reconstruction error. Watts/PF reconstruction is only a fallback for feeds
    // where the register is absent. (In the current feed Max_Demand is all-zero,
    // so it falls back to the 30-min reconstruction — get the Pi to log this
    // register to hit the invoice MD exactly.)
    const registerMd  = sorted.reduce((m, r) => Math.max(m, safe(r.maxDemandMeter)), 0);
    // The MD register is stored in VA on this meter model (same scale as the VAh
    // register) → /1000 to kVA. Guard the scale: only treat a value as VA when it
    // is large enough that /1000 lands in a plausible kVA band; a feed that already
    // stores kVA is used as-is.
    const registerKva = registerMd > 10_000 ? registerMd / 1000 : registerMd;
    peakKva = registerKva > 0 ? registerKva : maxDemandKvaOf(sorted, MD_BLOCK_MS_30);
  }


  // ── Rate tier (flat for WBSEDCL, 3-tier for TGSPDCL) ─────────────────────
  const rates         = tariff.getTariffRates(peakKva, CD);
  const rateTierLabel = tariff.getTierLabel(peakKva, CD);

  // ── Per-period energy breakdown ───────────────────────────────────────────
  const todPeriods: ToDPeriod[] = ['NORMAL', 'PEAK', 'OFF-PEAK'];
  const pfWtd = weightedPF(sorted);

  // Per-period trapezoidal integral — used as the ToD SPLIT RATIO only.
  const integralByPeriod: Record<ToDPeriod, number> = { NORMAL: 0, PEAK: 0, 'OFF-PEAK': 0 };
  for (const p of todPeriods) integralByPeriod[p] = unitDelivered(periodRows(sorted, p));
  const integralTotal = todPeriods.reduce((s, p) => s + integralByPeriod[p], 0);

  // TGSPDCL: pin the TOTAL kVAh to the VAh register (what the DISCOM invoices),
  // and distribute it across ToD periods using the integral's proportions. This
  // recovers the energy consumed during >1h logger gaps that the raw integral
  // drops. WBSEDCL (kWh) path is unchanged: unitScale = 1, so it stays integral.
  const registerKvah = isKvah ? registerKvahDelivered(sorted) : 0;
  const unitScale     = (isKvah && integralTotal > 0 && registerKvah > 0)
    ? registerKvah / integralTotal
    : 1;

  let totalUnit = 0;
  const periods: BillPeriodBreakdown[] = todPeriods.map((p) => {
    const pr             = periodRows(sorted, p);
    // unit = kVAh (TGSPDCL) or kWh (WBSEDCL)
    // rateRsUnit = Rs/kVAh (TGSPDCL) or Rs/kWh (WBSEDCL)
    // energyChargeRs = unit [kVAh] × rateRsUnit [Rs/kVAh] = Rs  ✓
    // TGSPDCL: integral proportion rescaled to the register total (see above).
    const unit           = integralByPeriod[p] * unitScale;
    totalUnit += unit;
    const rateRsUnit     = rates[p];
    const energyChargeRs = unit * rateRsUnit;
    // Per-period average PF — average the PF_Avg readings that fall inside THIS
    // ToD window (energy-weighted where possible). Periods with too little data
    // to compute their own PF inherit the overall weighted PF so they don't get
    // a spurious penalty from an empty window.
    const pfPeriodRaw    = weightedPF(pr);
    const pfPeriod       = pfPeriodRaw > 0 ? pfPeriodRaw : pfWtd;
    // PF rebate/surcharge applied per ToD period using THAT period's own PF
    // (WBSEDCL §3.2); 0 for TGSPDCL.
    const pfAdjPct       = tariff.hasPfAdjustment ? tariff.pfAdjustmentPct(pfPeriod, p) : 0;
    const pfAdjRs        = energyChargeRs * (pfAdjPct / 100);
    return {
      period: p, kvah: isKvah ? unit : 0, rateRsKvah: rateRsUnit, energyChargeRs,
      pfAvg: pfPeriod, pfAdjPct, pfAdjRs,
      kwh: isKvah ? energyDelivered(pr) : unit,
      rateRsKwh: rateRsUnit,
    };
  });

  const ecTotalRs    = periods.reduce((s, p) => s + p.energyChargeRs, 0);
  // pfAdjTotalRs sign convention (consumed by the page): negative = saving.
  const pfAdjTotalRs = -(periods.reduce((s, p) => s + p.pfAdjRs, 0));
  const totalKwh     = periods.reduce((s, p) => s + p.kwh, 0);

  // ── Telangana ToD penalty / rebate breakdown ──────────────────────────────
  // TGSPDCL billing model (§3):
  //   Base Energy Charge = totalKvah × Normal rate
  //   ToD Peak Penalty   = peakKvah  × (peakRate   − normalRate)
  //   ToD Off-Peak Rebate= offpkKvah × (normalRate − offpeakRate)  [positive = saving]
  //   ecTotal = baseEC + peakPenalty − offpeakRebate
  //            = same numerical result as per-period sum, different display
  const normalRate          = rates['NORMAL'];
  const peakRateDiff        = rates['PEAK']     - normalRate;   // positive (premium)
  const offpeakRateDiff     = normalRate - rates['OFF-PEAK'];   // positive (discount)
  const todPeakPeriod       = periods.find(p => p.period === 'PEAK');
  const todOffpeakPeriod    = periods.find(p => p.period === 'OFF-PEAK');
  const todPeakKvah         = isKvah ? (todPeakPeriod?.kvah   ?? 0) : 0;
  const todOffpeakKvah      = isKvah ? (todOffpeakPeriod?.kvah ?? 0) : 0;
  const ecBaseRs            = isKvah ? totalUnit * normalRate : ecTotalRs;
  const todPeakPenaltyRs    = isKvah ? todPeakKvah    * peakRateDiff    : 0;
  const todOffpeakRebateRs  = isKvah ? todOffpeakKvah * offpeakRateDiff : 0;
  // Verify: ecBaseRs + todPeakPenaltyRs − todOffpeakRebateRs ≈ ecTotalRs (floating pt tol.)

  // ── Load Factor rebate (WBSEDCL only) ─────────────────────────────────────
  const lfPct      = loadFactor(sorted);
  const lfRebateRs = tariff.hasLfRebate ? -(tariff.lfRebateRsPerKwh(lfPct) * totalKwh) : 0;

  // ── Demand charges ────────────────────────────────────────────────────────
  // PDF BI (WBSEDCL): Normal kVA = round(max(Max kVA, 0.85×CD))
  //   NO min(MD, CD) cap — when MD > CD, Normal kVA = MD (full demand).
  //   The additional kVA (BJ) adds extra charge on top, it does NOT reduce Normal kVA.
  //
  // TGSPDCL AP:        Normal kVA = round(max(min(MD, CD), 0.80×CD))
  //   min(MD, CD) cap IS correct here — excess above CD is separately charged as addlKva.
  //
  // BUG FIX (WB): previous code used max(min(peakKva,CD), minCD) for both states,
  // which wrongly capped WB normalKva at CD when MD > CD — understating Normal DC.
  // Example: MD=220, CD=176, minCD=149.6
  //   Before fix: normalKva = round(max(min(220,176), 149.6)) = 176 → DC = 176×270 = 47520
  //   After fix:  normalKva = round(max(220, 149.6))          = 220 → DC = 220×270 = 59400
  //   Error = Rs 11,880 undercharge per billing cycle.
  const normalKva = !isKvah
    ? Math.round(Math.max(peakKva, minCD))                    // WB spec BI: round(max(Max kVA, 0.85×CD))
    : Math.round(Math.max(Math.min(peakKva, CD), minCD));     // TGSPDCL spec AP: round(max(min(MD,CD), 0.80×CD))
  // PDF BJ: Additional kVA uses MD_Peak only for WB (not overall BH max).
  // TGSPDCL: uses overall peakKva (original unchanged behaviour).
  const addlKva   = Math.round(Math.max(0, (!isKvah ? mdKvaPeak : peakKva) - CD));

  const dcNormalRs = normalKva * tariff.demandChargeRsKva;
  const dcPenalRs  = addlKva   * tariff.demandPenalRsKva;
  const dcRs       = dcNormalRs + dcPenalRs;

  // ── Sub total ─────────────────────────────────────────────────────────────
  // TGSPDCL (kVAh): Sub Total = Energy Charges + Demand + Electricity Duty
  //   (all charges EXCEPT Customer Charges)
  // WBSEDCL (kWh):  Sub Total = Energy + PF adj + LF rebate + Demand (before ED)
  //   (keeps existing behaviour — WB ED is pct-of-bill computed after sub total)
  const energyAndDemandRs = Math.max(0, ecTotalRs + pfAdjTotalRs + lfRebateRs + dcRs);

  // ── Fixed/statutory charges ───────────────────────────────────────────────
  const electricityDutyRs = tariff.electricityDutyModel === 'pct-of-bill'
    ? energyAndDemandRs * tariff.electricityDutyValue
    : totalUnit * tariff.electricityDutyValue;            // per-kVAh

  // For TGSPDCL: Sub Total includes ED (all charges except Customer Charges)
  // For WBSEDCL: Sub Total stays as energy+demand (before ED) — existing behaviour
  const subTotalRs = isKvah
    ? energyAndDemandRs + electricityDutyRs               // TGSPDCL: Sub Total includes ED
    : energyAndDemandRs;                                   // WBSEDCL: Sub Total before ED

  // TGSPDCL: customer charges are a flat ₹3,500/month, never pro-rated.
  // WBSEDCL: customerChargesRsMonth = 0, so pro-ration is moot; formula unchanged.
  const customerChargesRs = isKvah
    ? tariff.customerChargesRsMonth                          // TGSPDCL: full flat amount
    : tariff.customerChargesRsMonth * Math.min(monthFraction, 1); // WBSEDCL: keep as-is
  // Meter rental is always the full monthly charge — it is a fixed line item
  // on every bill regardless of how many days have been metered so far.
  const meterRentalRs     = tariff.meterRentalRsMonth;

  // ACD (Additional Consumption Deposit) Surcharge — deposit-linked pass-through,
  // not a telemetry-derived quantity. TGSPDCL only; 0 unless supplied by tariff.
  const acdSurchargeRs = isKvah ? (acdSurchargeRsOverride ?? tariff.acdSurchargeRs ?? 0) : 0;

  // TGSPDCL: Gross Total = Sub Total (includes ED) + Customer Charges + ACD Surcharge
  // WBSEDCL: Gross Total = Sub Total + ED + Meter Rental + Customer Charges (unchanged)
  const grossPayableRs = isKvah
    ? subTotalRs + customerChargesRs + acdSurchargeRs      // TGSPDCL: Sub Total already has ED
    : energyAndDemandRs + electricityDutyRs + customerChargesRs + meterRentalRs; // WBSEDCL unchanged
  const tprRs          = -(grossPayableRs * tariff.tprFraction);
  const lpscRs         = grossPayableRs * tariff.lpseFraction;
  const netPayableRs   = grossPayableRs + tprRs; // LPSC only applies if paid late

  // ── Projection ────────────────────────────────────────────────────────────
  let projectedKvah: Record<ToDPeriod, number> | undefined;
  let projectedBillRs: number | undefined;
  let projectedGrossEcRs: number | undefined;
  let projectedPfAdjRs: number | undefined;
  let projectedLfAdjRs: number | undefined;
  let projectedEdRs: number | undefined;
  let projectedTprRs: number | undefined;

  if (daysPassed && daysPassed > 0 && daysTotal && daysTotal > daysPassed) {
    const daysRemaining = daysTotal - daysPassed;
    projectedKvah = {} as Record<ToDPeriod, number>;
    let projEC = 0;
    let projPfAdj = 0;

    for (const p of periods) {
      const unit       = isKvah ? p.kvah : p.kwh;
      const dailyAvg   = unit / daysPassed;
      const projected  = unit + dailyAvg * daysRemaining;
      projectedKvah[p.period] = projected;
      const ec = projected * p.rateRsKvah;
      projEC += ec;
      projPfAdj += ec * (p.pfAdjPct / 100);
    }

    const projTotalUnit  = Object.values(projectedKvah).reduce((s, v) => s + v, 0);
    const projPfAdjRs    = -projPfAdj;
    const projLfRebateRs = tariff.hasLfRebate
      ? -(tariff.lfRebateRsPerKwh(lfPct) * (totalKwh / (daysPassed || 1)) * (daysTotal || 1))
      : 0;
    const projEnergyAndDemand = Math.max(0, projEC + projPfAdjRs + projLfRebateRs + dcRs);
    const projED         = tariff.electricityDutyModel === 'pct-of-bill'
      ? projEnergyAndDemand * tariff.electricityDutyValue
      : projTotalUnit * tariff.electricityDutyValue;
    // TGSPDCL: projected Sub Total includes ED; WB: doesn't
    const projSubTotal   = isKvah
      ? projEnergyAndDemand + projED
      : projEnergyAndDemand;
    const projCustomer   = tariff.customerChargesRsMonth + tariff.meterRentalRsMonth;
    // TGSPDCL: Gross = projSubTotal (has ED) + customer + ACD; WB: add ED separately
    // ACD is a flat monthly deposit surcharge, so the full amount carries into the
    // projected month-end (same treatment as customer charges).
    const projGross      = isKvah
      ? projSubTotal + projCustomer + acdSurchargeRs
      : projEnergyAndDemand + projED + projCustomer;
    projectedTprRs       = -(projGross * tariff.tprFraction);
    projectedBillRs      = projGross + projectedTprRs;
    projectedGrossEcRs   = projEC;
    projectedPfAdjRs     = -projPfAdjRs; // positive = rebate (page convention)
    projectedLfAdjRs     = -projLfRebateRs;
    projectedEdRs        = projED;
  }

  return {
    spanHours, monthFraction,
    periods, ecTotalRs, totalKvah: isKvah ? totalUnit : 0, totalKwh: isKvah ? 0 : totalUnit,
    pfWeighted: pfWtd, pfAdjTotalRs,
    lfPct, lfRebateRs,
    peakKva, mdKvaNormal, mdKvaPeak, mdKvaOffpeak,  // PDF BH / BE / BF / BG
    normalKva, addlKva,
    dcNormalRs, dcPenalRs, dcRs, dcTotalRs: dcNormalRs + dcPenalRs,
    electricityDutyRs, customerChargesRs, acdSurchargeRs, meterRentalRs,
    subTotalRs, grossPayableRs, lpscRs, netPayableRs,
    rateTierLabel,
    // Telangana ToD breakdown
    todPeakKvah, todOffpeakKvah,
    ecBaseRs, todPeakPenaltyRs, todOffpeakRebateRs,
    projectedKvah, projectedBillRs,
    projectedGrossEcRs, projectedPfAdjRs, projectedLfAdjRs, projectedEdRs, projectedTprRs,
    // Legacy compat
    mdPenaltyRs:    dcPenalRs,
    chargeableKva:  normalKva,
    netBeforeEdRs:  subTotalRs,
    tprRs,
  };
}

/** Re-export for billing module consumers. */
export { costRupees as estimateCostRs, powerFactor } from './calculations';